import * as React from "react";
import styled from "@emotion/styled";
import messages from "@/messages";
import breakpoint from "@/utils/breakpoint";
import theme from "@/theme";
import classNames from "classnames";
import ContractContext, { initContractStore } from "@/contexts/Contract";
import contract from "@/utils/contract";
import { useTheme } from "@/hooks/useTheme";
import { useGoogleTagManagerId } from "@/hooks/useGoogleTagManagerId";
import { usePrivacyPolicyLabelText } from "@/hooks/usePrivacyPolicyLabelText";
import { SRE_AI_PARTNERS_LINK } from "@/constants/Footer";

const Wrapper = styled.div`
  margin: auto 0 0;
  width: 100%;
`;

const StyledFooter = styled.footer`
  width: 100%;
  background: #6d7472;
  padding: 10px;
  &.override {
    background: #ffffff;
  }
`;

const PolicyArea = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  margin: 30px 0 40px 0;
  ${breakpoint.lessThan("sm")`
    margin: 20px 0 22px 0;
  `}
  &.override {
    margin: 50px 0 30px 0;
    ${breakpoint.lessThan("sm")`
    margin: 40px 0 30px 0;
  `}
  }
`;

const ContentArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-wrap: wrap;
  color: ${theme.main.white};
  &.override {
    width: 65%;
    margin: auto;
    ${breakpoint.lessThan("sm")`
    width: 100%;
  `}
  }
`;

const Content = styled.p`
  font-size: 14px;
  line-height: 1;
  color: ${theme.main.white};
  text-align: center;
  padding: 0 6px;

  ${breakpoint.lessThan("sm")`
    font-size: 12px;
    line-height: 14px;
    font-weight: 400;
  `}
`;

const Content1 = styled(Content)`
  margin: 0 0 22px;
  &.override {
    color: #707070;
    font-size: 12px;
  }
`;

const Content2 = styled(Content)`
  margin: 0 0 30px;
  line-height: 1.5;
  &.override {
    margin: 0 0 41px;
    color: #707070;
    font-size: 12px;
    ${breakpoint.lessThan("sm")`
    margin: 0 0 40px;
  `}
  }
`;

const Content3 = styled(Content)`
  margin: 0 0 10px;
  line-height: 1.5;
  &.override {
    margin: 0 0 50px;
    color: #707070;
    font-size: 12px;
  }
`;

const Link = styled.a`
  color: ${theme.main.white};
  font-size: 14px;
  line-height: 14px;
  margin: 0 10px;
  ${breakpoint.lessThan("sm")`
    font-size: 12px;
    line-height: 14px;
  `}
  &.override {
    font-weight: 700;
    color: #707070;
    ${breakpoint.lessThan("sm")`
    font-size: 14px;
  `}
  }
  &.cookieLink {
    font-size: 12px;
    font-weight: 700;
    color: #707070;
  }
  &.noBold {
    font-size: 12px;
    font-weight: 400;
    margin: 0;
  }
`;

const PcBreak = styled.br`
  display: block;
  ${breakpoint.lessThan("sm")`
    display: none;
  `}
`;

const sanitize = (str: string): string => str.replace(/</g, "&lt;").replace(/>/g, "&gt;");

const Footer = () => {
  let { isNewTheme } = useTheme();
  const { googleTagManagerId } = useGoogleTagManagerId();
  const { PRIVACY_POLICY_LABEL_TEXT } = usePrivacyPolicyLabelText();
  const {
    state: { privacyPolicyLink, termsLink, copyright, companyName, cookieLink },
  } = React.useContext(ContractContext) || initContractStore(contract);

  const isCssOverride = classNames({
    override: isNewTheme,
  });

  return (
    <Wrapper>
      <StyledFooter className={isCssOverride}>
        <PolicyArea className={isCssOverride}>
          {privacyPolicyLink && (
            <Link target="_blank" href={privacyPolicyLink} className={isCssOverride}>
              {PRIVACY_POLICY_LABEL_TEXT}
            </Link>
          )}
          <Link target="_blank" href={termsLink} className={isCssOverride}>
            {messages.terms}
          </Link>
        </PolicyArea>
        <ContentArea className={isCssOverride}>
          <Content1 className={isCssOverride}>
            本ウェブサイトの運営は、
            <Link target="_blank" href={SRE_AI_PARTNERS_LINK} className={`${isCssOverride} noBold`}>
              SRE AI Partners株式会社
            </Link>
            に委託して行っています。
          </Content1>
          {googleTagManagerId && (
            <React.Fragment>
              <Content2 className={isCssOverride}>
                本ウェブサイトで{companyName}
                は、お客様の利便性の向上及びサービスの品質維持・向上を目的として、Cookieを使用しています。
                <PcBreak />
                本ウェブサイトの閲覧を続行した場合は、Cookieの使用に同意したものとします。詳細につきましては、
                <Link target="_blank" href={cookieLink} className={isNewTheme ? "cookieLink" : ""}>
                  {messages.cookiePolicy}
                </Link>
                をご確認ください。
              </Content2>
            </React.Fragment>
          )}
          {copyright && (
            <Content3 className={isCssOverride} dangerouslySetInnerHTML={{ __html: sanitize(copyright) }} />
          )}
        </ContentArea>
      </StyledFooter>
    </Wrapper>
  );
};

export default Footer;
